import {
  AreaChart, Area, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid,
} from "recharts";
import { P, FF } from "../../styles/tokens";
import { fmt, sum } from "../../utils/format";
import { MESES, MESES_CORTO } from "../../constants/months";
import { Card, Pill, Heading, Label, Amount, CustomTooltip } from "../ui";

export default function ProyeccionView({ data, activeMonth, metas, ahorradoPorMeta, mobile }) {
  const mesesConDatos=[...Array(activeMonth+1)].map((_,i)=>i).filter(i=>sum(data[i].ingresos)>0);
  const ahorros=mesesConDatos.map(i=>data[i].ahorroReal||0);
  const promedio=ahorros.length>0?ahorros.reduce((a,b)=>a+b,0)/ahorros.length:0;
  let acc=0;
  const acumulado=mesesConDatos.map(i=>{ const sav=data[i].ahorroReal||0; acc+=sav; return {mes:MESES_CORTO[i],acumulado:acc}; });
  const proyeccion=[]; let pacc=acc;
  for(let i=1;i<=6;i++){ const mi=activeMonth+i; if(mi>=12) break; pacc+=promedio; proyeccion.push({mes:MESES_CORTO[mi],proyectado:Math.max(pacc,0)}); }
  const chartData=[...acumulado.map(d=>({mes:d.mes,acumulado:d.acumulado})),...proyeccion.map(d=>({mes:d.mes,proyectado:d.proyectado}))];
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      <div style={{ display:"grid", gridTemplateColumns:mobile?"1fr 1fr":"repeat(3,1fr)", gap:14 }}>
        {[
          { label:"Ahorro promedio/mes", val:promedio, t:P.ahorro, icon:"💰", sub:`${mesesConDatos.length} ${mesesConDatos.length===1?"mes":"meses"} registrados` },
          { label:"Acumulado al cierre",  val:acc,     t:P.fijos,  icon:"📦", sub:`hasta ${MESES[activeMonth]}` },
          { label:"Proyectado fin de año",val:Math.max(pacc,0), t:P.ingresos, icon:"🔮", sub:"a ritmo actual", wide:mobile },
        ].map(c=>(
          <Card key={c.label} style={c.wide?{gridColumn:"1/-1"}:{}}>
            <Label>{c.icon} {c.label}</Label>
            <div style={{ marginTop:12, marginBottom:6 }}><Amount size={26} token={c.t}>{fmt(c.val)}</Amount></div>
            <div style={{ fontSize:11, color:P.textXs, fontFamily:FF }}>{c.sub}</div>
          </Card>
        ))}
      </div>
      {chartData.length>1&&(
        <Card>
          <Heading>Ahorro acumulado + proyección</Heading>
          <p style={{ fontSize:12, color:P.textSm, fontFamily:FF, marginTop:-12, marginBottom:20 }}>Línea punteada = ritmo proyectado</p>
          <ResponsiveContainer width="100%" height={mobile?180:210}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="gA" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={P.ahorro.mid} stopOpacity={0.15}/>
                  <stop offset="95%" stopColor={P.ahorro.mid} stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="gP" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={P.ingresos.mid} stopOpacity={0.12}/>
                  <stop offset="95%" stopColor={P.ingresos.mid} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={P.border} vertical={false} />
              <XAxis dataKey="mes" tick={{ fontSize:11, fill:P.textSm, fontFamily:FF }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize:10, fill:P.textXs }} axisLine={false} tickLine={false} width={44} tickFormatter={v=>v>=1000?`$${(v/1000).toFixed(0)}k`:`$${v}`} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="acumulado" name="Acumulado" stroke={P.ahorro.mid} strokeWidth={2.5} fill="url(#gA)" dot={{ fill:P.ahorro.mid, r:4, strokeWidth:0 }} connectNulls />
              <Area type="monotone" dataKey="proyectado" name="Proyección" stroke={P.ingresos.mid} strokeWidth={2} strokeDasharray="6 4" fill="url(#gP)" dot={{ fill:P.ingresos.mid, r:3, strokeWidth:0 }} connectNulls />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      )}
      {metas.filter(m=>m.objetivo>0).length>0&&(
        <Card>
          <Heading>🎯 ¿Cuándo alcanzas tus metas?</Heading>
          {metas.filter(m=>m.objetivo>0).map(meta=>{
            const acum=ahorradoPorMeta[meta.id]||0, falta=Math.max(meta.objetivo-acum,0);
            const mesesNec=promedio>0?Math.ceil(falta/promedio):null;
            const pct=meta.objetivo>0?Math.min((acum/meta.objetivo)*100,100):0;
            const mesLlegada=mesesNec!==null?activeMonth+mesesNec:null;
            const mesNombre=mesLlegada!==null&&mesLlegada<12?MESES[mesLlegada]:mesesNec?`~${mesesNec} meses`:null;
            return (
              <div key={meta.id} style={{ marginBottom:20, paddingBottom:20, borderBottom:`1px solid ${P.border}` }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <span style={{ fontSize:20 }}>{meta.icono}</span>
                    <span style={{ fontWeight:700, fontSize:14, color:P.text, fontFamily:FF }}>{meta.nombre||"Sin nombre"}</span>
                  </div>
                  {pct>=100?<Pill label="✓ Completada" token={P.ingresos}/>:mesNombre?<Pill label={`📅 ${mesNombre}`} token={P.ahorro}/>:promedio<=0?<Pill label="Sin ahorro" token={P.danger}/>:null}
                </div>
                <div style={{ height:8, background:P.bg, borderRadius:99, marginBottom:8, overflow:"hidden" }}>
                  <div style={{ height:"100%", borderRadius:99, background:`linear-gradient(90deg,${P.ahorro.mid},${P.ahorro.base})`, width:`${pct}%`, transition:"width .5s" }} />
                </div>
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:P.textXs, fontFamily:FF }}>
                  <span>{fmt(acum)} ahorrado</span><span>Meta: {fmt(meta.objetivo)}</span>
                </div>
              </div>
            );
          })}
        </Card>
      )}
    </div>
  );
}
